import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
  width?: number;
  onWidthChange?: (width: number) => void;
}

const Sidebar = ({
  isOpen,
  onToggle,
  width = 280,
  onWidthChange,
}: SidebarProps) => {
  const location = useLocation();
  const { isAuthenticated, hasRole } = useAuth();

  const getActiveLinkStyle = (path: string) => {
    const isActive = location.pathname === path;
    return {
      backgroundColor: isActive ? 'var(--accent)' : 'transparent',
      color: isActive ? 'white' : 'var(--text-primary)',
      borderLeft: isActive
        ? '4px solid var(--accent)'
        : '4px solid transparent',
    };
  };

  interface SidebarNavItem {
    path: string;
    icon: string;
    label: string;
    requiresAuth: boolean;
    children?: { path: string; icon: string; label: string }[];
  }

  const sidebarItems: SidebarNavItem[] = [
    {
      path: '/dashboard',
      icon: '📊',
      label: 'Dashboard',
      requiresAuth: true,
    },
    {
      path: '/inventory',
      icon: '📦',
      label: 'Inventory',
      requiresAuth: true,
    },
    {
      path: '/transactions',
      icon: '📝',
      label: 'Transactions',
      requiresAuth: true,
    },
    {
      path: '/articles',
      icon: '🏷️',
      label: 'Articles',
      requiresAuth: true,
    },
    {
      path: '/parties',
      icon: '🤝',
      label: 'Parties',
      requiresAuth: true,
    },
    {
      path: '/warehouse',
      icon: '🏭',
      label: 'Warehouse',
      requiresAuth: true,
    },
    {
      path: '/orders/purchase',
      icon: '📥',
      label: 'Purchase',
      requiresAuth: true,
    },
    {
      path: '/pos',
      icon: '🧾',
      label: 'POS',
      requiresAuth: true,
    },
    {
      path: '/orders/sales',
      icon: '📤',
      label: 'Sales',
      requiresAuth: true,
    },
    {
      path: '/reports',
      icon: '📈',
      label: 'Reports',
      requiresAuth: true,
    },
    {
      path: '/audit-log',
      icon: '🕓',
      label: 'Audit Log',
      requiresAuth: true,
    },
    {
      path: '/settings',
      icon: '⚙️',
      label: 'Settings',
      requiresAuth: true,
    },
  ];

  const publicItems = [
    {
      path: '/',
      icon: '🏠',
      label: 'Home',
      requiresAuth: false,
    },
    {
      path: '/about',
      icon: 'ℹ️',
      label: 'About',
      requiresAuth: false,
    },
    {
      path: '/contact',
      icon: '📞',
      label: 'Contact',
      requiresAuth: false,
    },
  ];

  return (
    <>
      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-screen transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{
          width: `${width}px`,
          backgroundColor: 'var(--bg-primary)',
          borderRight: '1px solid var(--border)',
        }}
      >
        {/* Resize Handle */}
        {onWidthChange && (
          <div
            className="absolute top-0 right-0 w-1 h-full cursor-col-resize transition-colors"
            style={{
              backgroundColor: 'var(--border)',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = 'var(--accent)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = 'var(--border)';
            }}
            onMouseDown={e => {
              e.preventDefault();
              const startX = e.clientX;
              const startWidth = width;

              const handleMouseMove = (e: MouseEvent) => {
                const newWidth = Math.max(
                  200,
                  Math.min(500, startWidth + (e.clientX - startX))
                );
                onWidthChange(newWidth);
              };

              const handleMouseUp = () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
              };

              document.addEventListener('mousemove', handleMouseMove);
              document.addEventListener('mouseup', handleMouseUp);
            }}
          />
        )}
        {/* Sidebar Header */}
        <div className="p-4 border-b" style={{ borderColor: 'var(--border)' }}>
          <div className="flex items-center justify-between">
            <h2
              className="text-lg font-semibold m-0"
              style={{ color: 'var(--text-primary)' }}
            >
              Navigation
            </h2>
            <button
              onClick={onToggle}
              className="p-2 rounded-md hover:bg-opacity-10 transition-colors"
              style={{
                color: 'var(--text-primary)',
                backgroundColor: 'transparent',
                border: '1px solid var(--border)',
              }}
              aria-label="Close sidebar"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4">
          {/* Main Navigation Items */}
          {isAuthenticated && (
            <div className="px-3 mb-6">
              <h3
                className="px-3 mb-2 text-xs font-semibold uppercase tracking-wide"
                style={{ color: 'var(--text-secondary)' }}
              >
                Management
              </h3>
              <ul className="space-y-1 list-none m-0 p-0">
                {sidebarItems.map(item => {
                  const isParentActive = location.pathname.startsWith(item.path);
                  return (
                    <li key={item.path}>
                      <Link
                        to={item.path}
                        onClick={onToggle}
                        className="flex items-center px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 no-underline hover:bg-opacity-10"
                        style={getActiveLinkStyle(item.path)}
                      >
                        <span className="mr-3 text-lg">{item.icon}</span>
                        {item.label}
                      </Link>
                      {item.children && isParentActive && (
                        <ul className="mt-1 ml-4 space-y-1 list-none p-0">
                          {item.children.map(child => (
                            <li key={child.path}>
                              <Link
                                to={child.path}
                                onClick={onToggle}
                                className="flex items-center px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-200 no-underline hover:bg-opacity-10"
                                style={getActiveLinkStyle(child.path)}
                              >
                                <span className="mr-2">{child.icon}</span>
                                {child.label}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Public Pages */}
          <div className="px-3 mb-6">
            <h3
              className="px-3 mb-2 text-xs font-semibold uppercase tracking-wide"
              style={{ color: 'var(--text-secondary)' }}
            >
              General
            </h3>
            <ul className="space-y-1 list-none m-0 p-0">
              {publicItems.map(item => (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    onClick={onToggle}
                    state={item.path === '/' ? { intentional: true } : undefined}
                    className="flex items-center px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 no-underline hover:bg-opacity-10"
                    style={getActiveLinkStyle(item.path)}
                  >
                    <span className="mr-3 text-lg">{item.icon}</span>
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Admin Section */}
          {isAuthenticated && hasRole('admin') && (
            <div className="px-3">
              <h3
                className="px-3 mb-2 text-xs font-semibold uppercase tracking-wide"
                style={{ color: 'var(--text-secondary)' }}
              >
                Administration
              </h3>
              <ul className="space-y-1 list-none m-0 p-0">
                <li>
                  <Link
                    to="/admin"
                    onClick={onToggle}
                    className="flex items-center px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 no-underline hover:bg-opacity-10"
                    style={getActiveLinkStyle('/admin')}
                  >
                    <span className="mr-3 text-lg">⚙️</span>
                    Admin Panel
                  </Link>
                </li>
              </ul>
            </div>
          )}
        </nav>

        {/* Sidebar Footer */}
        <div className="p-4 border-t" style={{ borderColor: 'var(--border)' }}>
          <div className="text-center">
            <p
              className="text-xs m-0"
              style={{ color: 'var(--text-secondary)' }}
            >
              Sudo IMS v1.0.0
            </p>
            <p
              className="text-xs m-0 mt-1"
              style={{ color: 'var(--text-secondary)' }}
            >
              © {new Date().getFullYear()} All rights reserved
            </p>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
